import isEqual from 'lodash/isEqual';
import {useMemo} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import {usePrevious} from '../../../../../../common/shared/hooks/usePrevious';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {PageCounterStatus} from '../constant';
import {FrameSize} from '../type/FrameSize.interface';
import {useCountTriggerTextViewerCoreOption} from './useCountTriggerTextViewerCoreOption.hook';

export function usePageCounterStatus({
  productId,
  size,
}: {
  productId: number;
  size: FrameSize;
}): PageCounterStatus {
  const {currentHtmlInfo, isDataLoaded} = useTypedSelector(state => ({
    currentHtmlInfo: state.textViewer.viewData[productId]?.currentHtmlInfo,
    isDataLoaded: state.textViewer.viewData[productId]?.isDataLoaded,
  }));

  const option = useCountTriggerTextViewerCoreOption({
    size,
    isMobile: requestContext.requestInfo.display.isMobile,
  });

  const prevOption = usePrevious(option);
  const prevCurrentHtmlInfo = usePrevious(currentHtmlInfo);
  const prevIsDataLoaded = usePrevious(isDataLoaded);

  const shouldCountTotalChapters = useMemo(
    () =>
      (!isEqual(prevIsDataLoaded, isDataLoaded) && isDataLoaded) ||
      !isEqual(prevOption, option),
    [isDataLoaded, option, prevIsDataLoaded, prevOption]
  );

  const shouldCountCurrentPageCount = useMemo(
    () =>
      shouldCountTotalChapters ||
      !isEqual(prevCurrentHtmlInfo, currentHtmlInfo),
    [currentHtmlInfo, prevCurrentHtmlInfo, shouldCountTotalChapters]
  );

  if (!size.width || !size.height || !isDataLoaded) {
    return PageCounterStatus.NoCount;
  }

  if (shouldCountTotalChapters) {
    return PageCounterStatus.AllCount;
  }

  if (shouldCountCurrentPageCount) {
    return PageCounterStatus.OnlyCurrentPageCount;
  }

  return PageCounterStatus.NoCount;
}
